/** Owns camera tracks, including permissions resolved after the scanner was closed. */
export function createCameraSession(video, mediaDevices, onEnded) {
    let generation = 0;
    let stream = null;

    function stop() {
        generation++;

        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            stream = null;
        }

        video.pause();
        video.srcObject = null;
    }

    async function start(deviceId = '') {
        stop();
        const current = generation;
        const constraints = deviceId ? { deviceId: { exact: deviceId } } : { facingMode: { ideal: 'environment' } };
        let acquired = null;

        try {
            acquired = await mediaDevices.getUserMedia({
                audio: false,
                video: { ...constraints, width: { ideal: 1280 }, height: { ideal: 720 } },
            });

            if (current !== generation) {
                acquired.getTracks().forEach(track => track.stop());
                return false;
            }

            stream = acquired;
            stream.getVideoTracks().forEach(track => track.addEventListener('ended', () => {
                if (current === generation) {
                    stop();
                    onEnded();
                }
            }, { once: true }));
            video.srcObject = stream;
            await video.play();
            return current === generation;
        } catch (error) {
            if (acquired) {
                acquired.getTracks().forEach(track => track.stop());
            }

            if (current !== generation) {
                return false;
            }

            stop();
            throw error;
        }
    }

    return { start, stop };
}

export function normalizeQrIdentifier(value) {
    if (typeof value !== 'string') {
        return null;
    }

    const identifier = value.trim().toUpperCase();
    const pattern = /^[A-Z0-9]{6,128}$/;
    return pattern.test(identifier) ? identifier : null;
}

export function cameraErrorKey(error) {
    switch (error && error.name) {
        case 'NotAllowedError':
        case 'SecurityError':
            return 'scanner.denied';
        case 'NotFoundError':
        case 'DevicesNotFoundError':
            return 'scanner.not_found';
        case 'NotReadableError':
        case 'TrackStartError':
            return 'scanner.busy';
        default:
            return 'scanner.failed';
    }
}
